import type { BlockAnchorInfo, HtmlEditApplierDeps, HtmlEditApplierMode } from './shared';
import {
  documentIdSchema,
  getBlockCardAnchor,
  getIsCancellationError,
  stripSelectionMarkers,
} from './shared';
import { getIsPdfLocationAnchor } from '../../extract-paragraphs/pdf/locationAnchor';
import {
  AlineTransforms,
  applyInsertRowsOutput,
  generateSuggestedEditGroupId,
  parseAnchorId,
} from '../../slate-api';
import type { InsertRowsSuggestion } from '../redline-llm-output';
import type {
  CodeBlockRedlineEditError,
  CodeBlockRedlineInsert,
  CodeBlockRedlineItem,
} from '../redline-shapes';

/**
 * Apply one streamed `insert-rows` suggestion to the server-side editor and
 * convert it into the `CodeBlockRedlineItem` (a `redline-insert` or a
 * `redline-edit-error`) that the client will render. The reference row's own
 * `row-…` id identifies it. The card reuses the insert preview, with the
 * `<tr>` html wrapped in a `<table>` so it renders, and above/below mapped
 * onto the insert card's before/after.
 */
export async function applyInsertRowsSuggestion(
  suggest: InsertRowsSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem> {
  const { editorPool, errorReporter, logger, makeSource, paragraphState, userId } = deps;
  const { documentId, id: anchorId, position, reasoning } = suggest;
  const html = stripSelectionMarkers(suggest.html);
  const groupId = generateSuggestedEditGroupId();

  const insertRowsError = (message: string): CodeBlockRedlineEditError => ({
    documentId,
    groupId,
    locationAnchor: anchorId,
    message,
    type: 'redline-edit-error',
  });

  const buildInsertItem = (locationAnchor: string): CodeBlockRedlineInsert => ({
    documentId,
    groupId,
    html: `<table>${html}</table>`,
    locationAnchor,
    position: position === 'above' ? 'before' : 'after',
    reasoning,
    type: 'redline-insert',
  });

  /**
   * The model supplies `documentId` as a loose string, so it can arrive empty
   * or as an invented placeholder when no document is attached. An
   * unresolvable target is a per-edit problem, not a turn failure, so report
   * it as a `redline-edit-error` and let the rest of the stream proceed.
   */
  if (!documentIdSchema.safeParse(documentId).success) {
    const message = `RedlineAgent emitted an invalid documentId "${documentId}" for row "${anchorId}".`;
    logger.warn(message);
    return insertRowsError(message);
  }

  const parsed = parseAnchorId(anchorId);
  if (parsed.kind !== 'row') {
    const message = `RedlineAgent insert-rows requires the reference row's own "row-…" id, got "${anchorId}" (a ${parsed.kind} id).`;
    logger.error(message);
    errorReporter.captureMessage(message, {
      extra: { anchorId, documentId, position },
      level: 'error',
    });
    return insertRowsError(message);
  }

  let blockAnchorInfo: BlockAnchorInfo | undefined;
  try {
    blockAnchorInfo = await paragraphState.getBlockAnchorInfoByAnchorIdWithLazyLoad(
      documentId,
      anchorId
    );
  } catch (error) {
    /**
     * The lazy load throws for a valid-looking but unresolvable documentId,
     * such as a deleted document. That is one failed insertion rather than a
     * failed node. A user Stop must still propagate, so re-throw cancellation
     * before converting anything to an edit error.
     */
    if (getIsCancellationError(error)) throw error;
    const message = `RedlineAgent could not load document "${documentId}" for row "${anchorId}": ${error instanceof Error ? error.message : String(error)}`;
    logger.warn(message);
    errorReporter.captureMessage(message, {
      extra: { anchorId, documentId, position },
      level: 'warning',
    });
    return insertRowsError(message);
  }
  if (!blockAnchorInfo) {
    const message = `RedlineAgent could not find table row "${anchorId}" in document "${documentId}" to insert rows ${position} it.`;
    logger.error(message);
    const allAnchors = paragraphState.getAllLocationAnchorsForDocument(documentId);
    errorReporter.captureMessage(message, {
      extra: {
        anchorId,
        availableAnchorsCount: allAnchors.length,
        availableAnchorsSample: allAnchors.slice(0, 50),
        documentId,
        position,
      },
      level: 'error',
    });
    return insertRowsError(message);
  }

  /**
   * Preview mode returns the card without applying. PDF (Apryse) documents
   * are read-only with no table structure in the collab editor — the card is
   * anchored on the row's own coordinates — and signing-locked documents
   * (sent for signature or signed) must not receive new edits: both always
   * take this path.
   */
  if (
    mode === 'preview' ||
    getIsPdfLocationAnchor(blockAnchorInfo.locationAnchor) ||
    (await editorPool.getSigningLockState(documentId))
  ) {
    return buildInsertItem(getBlockCardAnchor(blockAnchorInfo));
  }

  try {
    const editor = await editorPool.getOrCreateEditor(documentId);
    AlineTransforms.withoutNormalizing(editor, () => {
      applyInsertRowsOutput(editor, {
        authorId: userId,
        groupId,
        html,
        locationAnchor: anchorId,
        position,
        reasoning,
        source: makeSource(groupId),
      });
    });
  } catch (error) {
    const message =
      error instanceof Error
        ? `RedlineAgent applyInsertRowsOutput threw for table row "${anchorId}": ${error.message}`
        : `RedlineAgent applyInsertRowsOutput threw for table row "${anchorId}".`;
    logger.error(message, error);
    errorReporter.captureException(error, { extra: { anchorId, documentId, position } });
    return insertRowsError(message);
  }

  return buildInsertItem(getBlockCardAnchor(blockAnchorInfo));
}
